import { usePosSetupStore } from "@/store/posSetupStore";
import { useDiscountCode } from "@/store/useDiscountCode";
import { getBrowserInfo } from "@/utils/BrowserInfo";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { waitForFieldChange } from "@/utils/waitForFieldChange";
import { cloneDeep } from "lodash";
import { RecordModel } from "pocketbase";
import { toast } from "vue3-toastify";
import { useCheckoutStore } from "./Docket";
import { useDocketStore } from "./DocketStore";
import { useUserRole } from "./useUserRole";

/**
 * Merges one or more source dockets into a target docket.
 * Moves all docket lines, preps, payments and receipts, then recalculates discounts.
 */
export const mergeDockets = async (sourceDocketIds: string[], targetDocketId: string): Promise<void> => {
  const expandStr = "docketLines_via_docket, docketLines_via_docket.product.category, docketPayments_via_docket, docketReceipts_via_docketID, preps_via_docket";

  const [targetDocket, ...sourceDockets] = await Promise.all([
    pb.collection("dockets").getOne(targetDocketId, { expand: expandStr }),
    ...sourceDocketIds.map(id => pb.collection("dockets").getOne(id, { expand: expandStr })),
  ]);

  const allDockets = [targetDocket, ...sourceDockets];
  const max = (field: string) => Math.max(...allDockets.map((d: any) => d[field] || 0));

  const mergedDiscounts = {
    discountProducts:  max("discountProducts"),
    discountBookings:  max("discountBookings"),
    discountEvents:    max("discountEvents"),
    discountFood:      max("discountFood"),
    discCodeProducts:  max("discCodeProducts"),
    discCodeBookings:  max("discCodeBookings"),
    discCodeEvents:    max("discCodeEvents"),
    discCodeFood:      max("discCodeFood"),
    discManProducts:   max("discManProducts"),
    discManBookings:   max("discManBookings"),
    discManEvents:     max("discManEvents"),
    discManFood:       max("discManFood"),
    discMemProducts:   max("discMemProducts"),
    discMemBookings:   max("discMemBookings"),
    discMemEvents:     max("discMemEvents"),
    discMemFood:       max("discMemFood"),
  };

  for (const sourceDocket of sourceDockets) {
    // Move docket lines
    for (const line of (sourceDocket.expand?.docketLines_via_docket || [])) {
      const newLineData = { ...line, id: null, docket: targetDocketId };
      const newLine = await pb.collection("docketLines").create(newLineData);

      await pb.collection("docketLines").update(line.id, { qty: 0, lineTotal: 0, lineDiscountTotal: 0 });

      const docketLineOptions = await pb.collection("docketLineOptions").getFullList({ filter: `docketLine = "${line.id}"` });
      for (const opt of docketLineOptions) {
        await pb.collection("docketLineOptions").create({ ...opt, id: undefined, docketLine: newLine.id });
      }

      const prepLines = await pb.collection("prepLines").getFullList({ filter: `docketLine = "${line.id}"` });
      for (const prepLine of prepLines) {
        await pb.collection("prepLines").update(prepLine.id, { docketLine: newLine.id });
      }
      let preppedQty = 0
      if (prepLines.length > 0) {
        preppedQty = prepLines.reduce((sum: number, pl: any) => sum + (pl.docketLineQty || 0), 0);
      }

      const linkedGiftVouchers = await pb.collection("giftVouchers").getFullList({ filter: `docketLine = "${line.id}"` });
      for (const giftVoucher of linkedGiftVouchers) {
        await pb.collection("giftVouchers").update(giftVoucher.id, { docketLine: newLine.id });
      }
      if (line.booking) {
        await pb.collection("bookings").update(line.booking, { docketLine: newLine.id });
      }
    }

    // Move preps, payments, receipts
    for (const prep of (sourceDocket.expand?.preps_via_docket || [])) {
      await pb.collection("preps").update(prep.id, { docket: targetDocketId });
    }
    for (const payment of (sourceDocket.expand?.docketPayments_via_docket || [])) {
      await pb.collection("docketPayments").update(payment.id, { docket: targetDocketId });
    }
    for (const receipt of (sourceDocket.expand?.docketReceipts_via_docketID || [])) {
      await pb.collection("docketReceipts").update(receipt.id, { docketID: targetDocketId });
    }

    await pb.collection("dockets").update(sourceDocket.id, {
      description: `${sourceDocket.description || ""} (Merged into #${targetDocket.docketNumber})`,
    });
  }

  // Recalculate line discounts on the target docket
  const updatedTarget = await pb.collection("dockets").getOne(targetDocketId, {
    expand: "docketLines_via_docket, docketLines_via_docket.product.category, docketPayments_via_docket",
  });

  let sortOrder = 0;
  for (const line of (updatedTarget.expand?.docketLines_via_docket || [])) {
    let combinedTypeDiscount = 0;
    switch (line.transactionType) {
      case "Product":
        combinedTypeDiscount = line.expand?.product?.expand?.category?.isFood
          ? mergedDiscounts.discountFood
          : mergedDiscounts.discountProducts;
        break;
      case "Booking Payment":
        combinedTypeDiscount = mergedDiscounts.discountBookings;
        break;
      case "Event":
        combinedTypeDiscount = mergedDiscounts.discountEvents;
        break;
    }

    const todAdjustment = line.todAdjustment || 0;
    const todDiscountValue = todAdjustment < 0 ? -todAdjustment : 0;
    const appliedDiscount = Math.max(combinedTypeDiscount, todDiscountValue, line.docketDiscount || 0);

    const basePrice = todAdjustment > 0 ? line.itemPrice * (1 + todAdjustment / 100) : line.itemPrice;
    const originalTotal = basePrice * line.qty;
    const discountedTotal = originalTotal * (1 - appliedDiscount / 100);
    const discountAmount = originalTotal - discountedTotal;

    let discountApplied = "None";
    if (appliedDiscount > 0) {
      discountApplied = (todDiscountValue > 0 && todDiscountValue >= combinedTypeDiscount && todDiscountValue >= (line.docketDiscount || 0))
        ? "TOD"
        : line.discountApplied || "Membership";
    }

    await pb.collection("docketLines").update(line.id, {
      sortOrder,
      docketDiscount: appliedDiscount,
      lineDiscountTotal: discountAmount.toFixed(2),
      discountApplied,
      lineTotal: discountedTotal,
    });
    sortOrder++;
  }

  await pb.collection("dockets").update(targetDocketId, {
    ...mergedDiscounts,
    description: `${targetDocket.description || ""} (Combined ${sourceDockets.map((d: any) => d.docketNumber).join(",")})`,
  });
};

export const useDocket = () => {
  interface CheckoutStore {
    processingItems: {
      inProcessing: any;
      processing: number;
      id: any;
      qty: number;
      lineTotal: number;
      itemPrice: number;
    }[];
    docketLinesItems: {
      isAvailable: any;
      id: any;
      qty: number;
      lineTotal: number;
      itemPrice: number;
    }[];
  }

  interface SelectedItem {
    inProcessing: number;
    id: any;
    qty: number;
    lineTotal: number;
    itemPrice: number;
    processing: number;
  }

  // Store
  const checkoutStore = useCheckoutStore();
  const userStore = useUserStore();
  const docketStore = useDocketStore();
  const discount = useDiscountCode();
  const userRole = useUserRole();
  const posSetupStore = usePosSetupStore();

  const {
    isFullScreenLoading,
    originalDocketItems,
    docketPaymentId,
    docketLinesItems,
    processingItems,
    posDiscount,
  } = storeToRefs(checkoutStore);

  const { paymentApproved } = docketStore;

  const { userId } = storeToRefs(userStore);
  const { discountCode, discountCodeData } = storeToRefs(discount);
  const { apps } = storeToRefs(userRole);
  const route = useRoute();

  // Data
  const docketId = ref<string | any>("");
  const email = ref<string | null>("");
  const isInitialized = ref<boolean>(false);
  const posSetup = ref<{} | null>(null);

  // Methods
  const PayWithTerminal = async (terminal: any) => {
    // the Terminal Payment button populates the docketPayments as pending, server will process payment and update this to approved
    const browserInfo = await getBrowserInfo();
    const data = {
      docket: docketId.value,
      paymentType: "Terminal",
      amount: Number(checkoutStore.payingNow).toFixed(2),
      cardTransactionReff: "Pending",
      status: "Pending",
      createdBy: userId.value,
      paymentTerminal: terminal?.id,
      webResponseLoc: window.location.origin,
      device: apps.value?.POS?.POSName ?? "",
      cashOutAmount: terminal?.cashOutAmount || 0,
      source: "POS",
      posDevice: posSetup.value?.id || null,
      browser: browserInfo.browser,
      OS: browserInfo.os,
      ipAddress: browserInfo.ip,
    };

    const record = await pb.collection("docketPayments").create(data);

    docketPaymentId.value = record.id;
    checkoutStore.paymentStatus = {
      type: record.status,
      message: "",
    };
    // update processing key in docketLine
    await updateItemStatus("Processing");
  };

  const recoverFromTerminal = async (paymentId:string, paymentStatus:string, message?:string) => {
    // Check if the payment type is a terminal
    // Check it its pending // Waiting // Busy
    docketPaymentId.value = paymentId;
    checkoutStore.paymentStatus = {
      type: paymentStatus,
      message: message,
    };
  }

  // update processed key in docketLines
  const updateProcessedStatus = async () => {
    try {
      let records: any[] = [];
      if (!records.length) {
        let filter = `docket="${docketId.value}"`;
        const response = await pb.collection("docketLines").getFullList({
          filter: filter,
        });
        if (response) {
          records = [...response];
        }
      } else {
        records = [...checkoutStore.processingItems];
      }
      records.map(async (item) => {
        // update processed key as number of quantity of item as its processed payment
        item.processed = item.processing;
        item.updatedBy = userId.value;
        await pb.collection("docketLines").update(item.id, item);
      });
    } catch (e) {}
  };

  // Payment with cash
  const PayWithCash = async (amtTender: any, source = "") => {
    const browserInfo = await getBrowserInfo();

    const data = {
      docket: docketId.value,
      paymentType: "Cash",
      amount: Number(checkoutStore.payingNow).toFixed(2),
      cardTransactionReff: "Pending",
      status: "Approved",
      amtTendered: amtTender,
      createdBy: userId.value,
      device: apps.value?.POS?.POSName ?? "",
      source,
      posDevice: posSetup.value?.id || null,
      browser: browserInfo.browser,
      OS: browserInfo.os,
      ipAddress: browserInfo.ip,
    };

    const record = await pb.collection("docketPayments").create(data);

    docketPaymentId.value = record.id;
    if (record && record?.status == "Approved") {
      await pb.collection("docketreceipts").create({
        docketID: docketId.value,
        openCashDraw: true,
        printer: posSetup.value?.receiptPrinter || '',
      });
      // Payment is done for the items the docketLines status will be processed
      await paymentApproved(docketId.value, record);
    }
    checkoutStore.amtTender = 0;
  };

  // Pay with invoice
  const payWithInvoice = async (params: {
    paymentType: string;
    cardTransactionReff: string;
    status: string;
    amtTendered: number | string;
    notes: string;
    source: "";
    device: string;
  }) => {
    try {
      const browserInfo = await getBrowserInfo();
      const data = {
        docket: docketId.value,
        paymentType: params.paymentType,
        amount: Number(checkoutStore.payingNow).toFixed(2),
        cardTransactionReff: params.cardTransactionReff,
        status: params.status,
        amtTendered: params?.amtTendered,
        notes: params.notes,
        createdBy: userId.value,
        source: params?.source,
        device: params?.device,
        posDevice: posSetup.value?.id || null,
        browser: browserInfo.browser,
        OS: browserInfo.os,
        ipAddress: browserInfo.ip,
      };

      const record = await pb.collection("docketPayments").create(data);
      if (record) {
        docketPaymentId.value = record.id;

        // update processing key in docketLine
        await paymentApproved(docketId.value, record);
      }
    } catch (e) {}
  };

  // Pay with Manual Terminal
  const payWithManualTerm = async (params: {
    paymentType: string;
    cardTransactionReff: string;
    status: string;
    amtTendered: number | string;
    description: string;
    paymentTerminal: string;
    paymentId: any;
  }) => {
    try {
      await pb.collection("dockets").update(docketId.value, {
        description: params.description,
      });

      const browserInfo = await getBrowserInfo();
      const data = {
        docket: docketId.value,
        paymentType: params.paymentType,
        amount: Number(checkoutStore.payingNow).toFixed(2),
        cardTransactionReff: params.cardTransactionReff,
        status: params.status,
        amtTendered: params?.amtTendered,
        paymentTerminal: params?.paymentTerminal,
        createdBy: userId.value,
        device: apps.value?.POS?.POSName ?? "",
        posDevice: posSetup.value?.id || null,
      };

      let record;
      if (params.paymentId) {
        data.updatedBy = userId.value;
        // Update existing payment if paymentId exists
        record = await pb.collection("docketPayments").update(params.paymentId, data);
      } else {
        // Create a new payment if paymentId does not exist
        data.browser = browserInfo.browser;
        data.OS = browserInfo.os;
        data.ipAddress = browserInfo.ip;
        record = await pb.collection("docketPayments").create(data);
      }
      if (record) {
        docketPaymentId.value = record.id;

        if (params.status == "Approved") {
          await paymentApproved(docketId.value, record);
        }
      }
    } catch (e) {}
  };

  // Virtual pay
  const payWithCreditCard = async (params: {
    paymentType: string;
    cardTransactionReff: string;
    status: string;
    amtTendered: number | string;
    cardHolderName: string;
    cardNumber: string;
    expiry: string;
    cvv: string;
    paymentTerminal?: string;
    surchargeFee?: number;
    source?: string;
  }) => {
    try {
      const browserInfo = await getBrowserInfo();
      const data = {
        docket: docketId.value,
        paymentType: params.paymentType,
        amount: Number(checkoutStore.payingNow).toFixed(2),
        cardTransactionReff: params.cardTransactionReff,
        status: params.status,
        amtTendered: params?.amtTendered,
        cardName: params?.cardHolderName,
        cardNumber: params?.cardNumber,
        cardExpiry: params?.expiry,
        cardCvc: params.cvv,
        createdBy: userId.value,
        paymentTerminal: params?.paymentTerminal,
        surchargeAmount: params?.surchargeFee,
        source: params?.source ?? "",
        webResponseLoc: window.location.origin,
        device: apps.value?.POS?.POSName ?? "",
        posDevice: posSetup.value?.id || null,
        browser: browserInfo.browser,
        OS: browserInfo.os,
        ipAddress: browserInfo.ip,
      };

      const record = await pb.collection("docketPayments").create(data);

      if (record) docketPaymentId.value = record.id;
    } catch (e) {
      console.error("Error in payWithCreditCard", e);
    }
  };

  // pay with gift voucher or coupons
  const applyValueToken = async (params: {
    status?: string;
    paymentType: "Coupon" | "Gift Voucher" | "Event Ticket";
    amount: number;
    identifier: string;
    qty: number;
    toPay?: number;
    notes?: string;
    type?: string;
    bookingId?: string;
    docketId?: string;
    totalPaid?: number;
    source?: string;
    giftValue?: any;
  }) => {
    try {
      // Set docket ID if provided
      if (params?.docketId) docketId.value = params.docketId;

      // Helper to get specific fields for each payment type
      const getPaymentSpecificFields = (params: any) => {
        switch (params.paymentType) {
          case "Coupon":
            return { coupon: params.identifier, couponQty: params.qty };
          case "Gift Voucher":
            return { giftVoucher: params.identifier };
          case "Event Ticket":
            return { eventTicket: params.identifier };
          default:
            return {};
        }
      };

      // Prepare data for payment record creation
      const browserInfo = await getBrowserInfo();
      const data = {
        docket: docketId.value,
        source: params?.source ?? "",
        paymentType: params.paymentType,
        amount: Number(checkoutStore.payingNow).toFixed(2),
        status: params.status,
        createdBy: userId.value,
        notes: params.notes,
        device: apps.value?.POS?.POSName ?? "",
        webResponseLoc: window.location.origin,
        posDevice: posSetup.value?.id || null,
        ...getPaymentSpecificFields(params),
        browser: browserInfo.browser,
        OS: browserInfo.os,
        ipAddress: browserInfo.ip,
      };

      // Snapshot the balance so we can poll for the AfterCreateSuccess hook's
      // deduction to land before callers refetch the coupon/gift voucher list.
      const balanceCollection = params.paymentType === "Coupon" ? "coupons" : params.paymentType === "Gift Voucher" ? "giftVouchers" : undefined;
      const balanceField = params.paymentType === "Coupon" ? "qtyRemaining" : "balanceRemaining";
      let balanceBefore: number | undefined;
      if (balanceCollection) {
        try {
          const before = await pb.collection(balanceCollection).getOne(params.identifier, {
            fields: `${balanceField},id`,
          });
          balanceBefore = Number(before[balanceField]);
        } catch (e) {
          console.error(`Error reading ${balanceCollection} balance before payment:`, e);
        }
      }

      // Create payment record and update balances
      const record = await pb.collection("docketPayments").create(data);
      docketPaymentId.value = record.id;

      // Check approval and booking due updates
      if (params.status === "Approved") await paymentApproved(docketId.value, record);

      if (params?.type === "Booking" && params?.bookingId) {
        // Update the Booking!!!
        let bookingCheck = await pb.collection("bookings").getOne(params.bookingId)
        const balanceRemaining = (params.toPay || checkoutStore.balanceToPay) - checkoutStore.payingNow;
        const due = balanceRemaining <= 0 ? 0 : Number(balanceRemaining).toFixed(2);

        await pb.collection("bookings").update(params.bookingId, {
          due,
          paid: (params.totalPaid || 0) + params.amount + (bookingCheck?.paid || 0),
        });
      }

      if (balanceCollection && balanceBefore !== undefined) {
        await waitForFieldChange(balanceCollection, params.identifier, balanceField, balanceBefore);
      }

      return record;
    } catch (e) {
      console.error(e);
    }
  };

  // Update requiresEmail in docketPayments
  const emailReceipt = async () => {
    if (docketId.value) {
      const data = {
        docketID: docketId.value,
        email: email.value,
        requiresEmail: true,
      };

      const response = await pb.collection("docketReceipts").create(data);

      if (response) {
        toast.success("Email Receipt Sent", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    }
  };

  // Update requiresPrinting in docketPayments
  const printReceipt = async (printer?: string) => {
    if (docketId.value) {
      const data = {
        docketID: docketId.value,
        email: email.value,
        requiresPrinting: true,
        printer: printer || posSetup.value?.receiptPrinter || '',
      };

      const response = await pb.collection("docketReceipts").create(data);

      if (response) {
        toast.success("Receipt Printed", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    }
  };

  // get the list of docketLine for docket
  const getDocketLinesItems = async (dId = null, productsMap?: any) => {
    try {
      // if not available take from param
      const docket_Id = route?.query?.did || dId;
      const response = await pb.collection("docketLines").getFullList({
        filter: `docket = '${docket_Id}'`,
        expand: "product,product.category,booking,booking.area,booking.activity,booking.options,docketLineOptions_via_docketLine",
        sort: "sortOrder",
      });

      if (response) {
        // save a print to store
        originalDocketItems.value = response;

        // available items update to store
        let availableItems = cloneDeep(response);

        availableItems.forEach(
          (el: {
            isAvailable: number;
            qty: any;
            processing: any;
            processed: any;
            isPaymentCompleted: boolean;
          }) => {
            if (el.qty === el.processed) {
              el.isPaymentCompleted = true;
            } else {
              el.isAvailable =
                el.qty === el.processed
                  ? el.qty - el.processed
                  : el.qty - el.processing;
              el.isPaymentCompleted = false;
            }
          },
        );

        // Options are included in the initial response via expand. Attach them and compute option-derived fields.
        for (const item of availableItems) {
          try {
            const options = item.expand?.docketLineOptions_via_docketLine || [];
            // Keep backward-compatible `options` field expected elsewhere
            item.options = options;
            item.selectedOptions = [];
            if (productsMap) {
              const product = productsMap[item?.product];
              if (product) {
                for (const option of options) {
                  item.selectedOptions.push({
                    name: option.option,
                    sellPrice: option.price,
                  });
                }
              }
            }

            item.optionsPrice = options.reduce(
              (sum: number, option: any) => sum + (option.price || 0),
              0,
            );
            item.lineText = item.lineText;
          } catch (error) {
            console.error(
              `Error processing options for docketLine ${item.id}:`,
              error,
            );
            item.options = [];
          }
        }

        docketLinesItems.value = availableItems.filter(
          (el: { qty: any; processing: any; isPaymentCompleted: any }) =>
            el.isPaymentCompleted ||
            (el.qty !== el.processing && !el.isPaymentCompleted),
        );
        // processing list items update to store
        let processingListItems: RecordModel[] = [];
        cloneDeep(response).forEach((el: any) => {
          if (
            el.processing &&
            el.qty > el.processed &&
            el.processing > el.processed
          ) {
            // let inProcessing = el.qty - el.processed;
            let inProcessing = el.processing - el.processed;
            el.inProcessing = inProcessing;
            processingListItems.push(el);
          }
        });
        processingItems.value = cloneDeep(processingListItems);
      }
    } catch (e) {}
  };

  // Update docketLine item status to processed when payment done from items tab
  const updateItemStatus = async (status: string) => {
    // not required which from other tab payment
    if (checkoutStore.activePaymentTab !== 2) {
      return;
    }

    let records = [];
    //when process one or more item from item tab
    let findItemsInProcessing = checkoutStore.processingItems;

    if (findItemsInProcessing.length) {
      records = findItemsInProcessing;
    } else {
      let filter = `docket="${docketId.value}"`;

      records = await pb.collection("docketLines").getFullList({
        filter: filter,
      });
    }

    records.forEach(async (item) => {
      // update processed key as number of quantity of item as its processed payment
      if (status === "Processing") {
        item.processing = item.inProcessing + item.processed;
        item.updatedBy = userId.value;
      }

      await pb.collection("docketLines").update(item.id, item);
    });
  };

  // changes status for the items
  const changeItemsStatus = async () => {
    checkoutStore.processingItems.forEach(async (item) => {
      const record = await pb.collection("docketLines").getFirstListItem(
        `id="${item?.id}" && product="${item?.product}"`
      );

      record.status = "";
      record.updatedBy = userId.value;
      await pb.collection("docketLines").update(record.id, record);
    });
  };

  // change item status to processed
  const updateStatusToProcessed = async () => {
    // not required which from other tab payment
    if (checkoutStore.activePaymentTab !== 2) {
      return;
    }
    let records = [];
    //when process one or more item from item tab
    let findItemsInProcessing = checkoutStore.processingItems;

    if (findItemsInProcessing.length) {
      records = findItemsInProcessing;
    } else {
      let filter = `docket="${docketId.value}"`;
      records = await pb.collection("docketLines").getFullList({
        filter: filter,
      });
    }
    records.forEach(async (item) => {
      // update processed key as number of quantity of item as its processed payment
      // item.processing = item.inProcessing + item.processed;
      item.processed = item.processing
        ? item.processing
        : item.inProcessing + item.processed;
      item.updatedBy = userId.value;
      await pb.collection("docketLines").update(item.id, item);
    });
    setTimeout(async () => {
      await getDocketLinesItems();
    }, 1000);
  };

  // get one docket
  const getOneDocket = async () => {
    try {
      let docket = await pb.collection("dockets").getOne(docketId.value, {
        expand: "discountCode",
      });

      // Older dockets never had subTotal/discount recalculated onto their record -
      // trigger a recalc and re-fetch so they're backfilled on load rather than
      // showing blank in the cart/checkout totals. updateDocket is a no-op for
      // Paid/Closed/Server-sourced dockets, so this is safe to call unconditionally.
      if (!docket.subTotal && !docket.discount) {
        await pb.send(`/bbapi/docketUpdate?did=${docket.id}`, { method: 'GET' });
        docket = await pb.collection("dockets").getOne(docketId.value, {
          expand: "discountCode",
        });
      }

      // add discount code and discount code data into discount store
      discountCode.value = docket?.discountCode || null;
      discountCodeData.value = docket?.expand?.discountCode || {};

      return docket;
    } catch(e){
      console.error("Error with Docket: ", e)
    }
    return {}

  };

  const refreshListItems = () => {
    checkoutStore.docketLinesItems = checkoutStore.docketLinesItems.filter(
      (el: any) => {
        if (el.hasOwnProperty("isAvailable") && el.isAvailable === 0) {
          return false;
        } else {
          return true;
        }
      },
    );
    checkoutStore.processingItems = checkoutStore.processingItems.filter(
      (el: any) => {
        if (el.hasOwnProperty("inProcessing") && el.inProcessing === 0) {
          return false;
        } else {
          return true;
        }
      },
    );
  };

  const handleSwapProductServices = (
    id: any,
    containerType: string,
    selectedItem: SelectedItem | undefined,
    checkoutStore: CheckoutStore | undefined,
  ): void => {
    // closure function for manage Flag to show virtual quantity
    const manageFlag = (
      items: any[],
      id: any,
      key: string,
      operation: string | undefined,
    ) => {
      items.map((el) => {
        if (el.id === id) {
          if (operation === "INC") {
            el[key] += 1;
          }
          if (operation === "DEC") {
            el[key] -= 1;
          }
        }
      });
    };

    // type: processing
    if (containerType === "Processing") {
      if (
        checkoutStore?.processingItems?.some((el) => el.id === selectedItem?.id)
      ) {
        // update flag in processing list
        manageFlag(checkoutStore.processingItems, id, "inProcessing", "INC");
      } else {
        if (selectedItem) {
          selectedItem.inProcessing = 1;
          checkoutStore?.processingItems.push(selectedItem);
        }
      }
      checkoutStore?.docketLinesItems.map((el) => {
        if (el.id === selectedItem?.id) {
          if (el.isAvailable) {
            el.isAvailable -= 1;
          } else {
            el.isAvailable = el.qty - 1;
          }
        }
      });
    } else {
      // type: available
      if (
        checkoutStore?.docketLinesItems?.some(
          (el) => el.id === selectedItem?.id,
        )
      ) {
        // update flag to available list
        manageFlag(checkoutStore.docketLinesItems, id, "isAvailable", "INC");
      } else {
        if (selectedItem) {
          selectedItem.inProcessing = 0;
          selectedItem["isAvailable"] = 1;
          checkoutStore?.docketLinesItems.push(selectedItem);
        }
      }
      // update flag in processing list
      checkoutStore?.processingItems.map((el) => {
        if (el.id === selectedItem.id) {
          if (el.inProcessing) {
            el.inProcessing -= 1;
          } else {
            el.inProcessing = el.qty - 1;
          }
        }
      });
    }
    // remove not existing list
    refreshListItems();
  };

  // docket get payment
  const getDocketPayment = async () => {
    try {
      const docketPayments = await pb
        .collection("docketPayments")
        .getList(1, 50, {
          filter: `docket = '${docketId.value}' && status = 'Approved'`,
        });

      // Use reduce to calculate the total payment amount
      const paymentDone = docketPayments?.items?.reduce(
        (total, payment) => total + payment.amount,
        0,
      );

      return paymentDone;
    } catch (e) {
      return 0; // Return 0 in case of error
    }
  };

  // Initialization logic inside the useDocket itself
  const init = async () => {
    if (!isInitialized.value) {
      isInitialized.value = true; // Ensure it's initialized only once
      if (route?.query?.did) {
        docketId.value = route?.query?.did;
      }
      if (localStorage.getItem("email"))
        email.value = localStorage.getItem("email");

      posSetup.value = await posSetupStore.load();
    }
  };

  const payWithCreditOPM = async (paymentRecord: any) => {
    try {
      isFullScreenLoading.value = true;
      const { docketID, amount, user, loggedInuser, accountType } = paymentRecord;

      // Create docket payment record
      const browserInfo = await getBrowserInfo();
      const paymentData = {
        docket: docketID,
        paymentType: accountType === 'loyalty' ? "User Loyalty" : "User Account",
        status: "Approved",
        amount,
        user,
        createdBy: loggedInuser,
        source: "POS",
        device: apps.value?.POS?.POSName ?? "",
        posDevice: posSetup.value?.id || null,
        browser: browserInfo.browser,
        OS: browserInfo.os,
        ipAddress: browserInfo.ip,
      };
      const payment = await pb.collection("docketPayments").create(paymentData);

      // Retrieve docket details
      const getDocket = await pb.collection("dockets").getOne(docketID, {
        fields: "id,status,total,due",
      });

      if (getDocket) {
        const { id } = getDocket;

        await paymentApproved(id, payment);
      }
    } catch (error) {
      console.error("Error in payWithCreditOPM:", error);
    } finally {
      isFullScreenLoading.value = false;
    }
  };

  watch(
    route,
    (newValue, oldValue) => {
      if (route?.query?.did) {
        docketId.value = route?.query?.did;
      }
    },
    { immediate: true },
  );

  // Automatically initialize when the store is accessed or used
  watchEffect(async () => {
    if (!isInitialized.value) {
      await init();
    }
  });

  return {
    docketId,
    docketPaymentId,
    email,
    posDiscount,
    docketLinesItems,
    payWithCreditOPM,
    PayWithTerminal,
    PayWithCash,
    printReceipt,
    emailReceipt,
    getDocketLinesItems,
    updateItemStatus,
    getOneDocket,
    updateStatusToProcessed,
    updateProcessedStatus,
    handleSwapProductServices,
    getDocketPayment,
    payWithInvoice,
    payWithManualTerm,
    payWithCreditCard,
    applyValueToken,
    recoverFromTerminal,
  };
};
